import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Users } from 'lucide-react';
import { useLanguage } from '@core/i18n';
import { useBooking } from '../state';
import { useBookingFlow } from '../flow';
import SummaryPill from './SummaryPill';
import type { BookingStepId } from '../flow/types';

interface BreadcrumbItem {
  stepId: BookingStepId;
  label: string;
  icon?: React.ReactNode;
}

interface BookingBreadcrumbsProps {
  show?: BookingStepId[];
  showDinersIcon?: boolean;
}

export function BookingBreadcrumbs({ show, showDinersIcon }: BookingBreadcrumbsProps) {
  const { t } = useTranslation('booking');
  const { dateLocale } = useLanguage();
  const { diners, selectedDate, service, selectedTime, flowType, wlDates } = useBooking();
  const { goToStep, currentStep, activeSteps } = useBookingFlow();

  const items = useMemo((): BreadcrumbItem[] => {
    const allItems: BreadcrumbItem[] = [
      {
        stepId: 'DINERS',
        label: t('PARTYSIZE_LABEL', { count: diners }),
        icon: showDinersIcon ? <Users size={16} /> : undefined,
      },
      {
        stepId: 'DATE',
        label: flowType === 'waiting_list' && wlDates.length > 0
          ? t('NEW_MODAL_WAITING_LIST_MULTIPLE_DATES')
          : selectedDate
            ? format(new Date(selectedDate + 'T12:00:00'), 'EEE d, MMM', { locale: dateLocale })
            : '',
      },
      {
        stepId: 'SERVICE',
        label: service ? t(`FILTER_STRING_${service.toUpperCase()}`) : '',
      },
      {
        stepId: 'TIME',
        label: selectedTime ?? '',
      },
    ];

    let filtered: BreadcrumbItem[];

    if (show) {
      filtered = allItems.filter(item => show.includes(item.stepId) && item.label);
    } else if (!currentStep) {
      return [];
    } else {
      const currentIndex = activeSteps.findIndex(s => s.id === currentStep.id);
      const stepsBeforeCurrent = activeSteps.slice(0, currentIndex).map(s => s.id);
      filtered = allItems.filter(item => {
        if (!item.label) return false;
        if (item.stepId === 'SERVICE') return !!service;
        return stepsBeforeCurrent.includes(item.stepId);
      });
    }

    // Max 3 pills: if TIME is present, drop SERVICE
    if (filtered.length > 3 && filtered.some(i => i.stepId === 'TIME')) {
      filtered = filtered.filter(i => i.stepId !== 'SERVICE');
    }

    return filtered;
  }, [show, showDinersIcon, diners, selectedDate, service, selectedTime, flowType, wlDates, t, dateLocale, currentStep, activeSteps]);

  if (items.length === 0) return null;

  return (
    <div className="flex items-center gap-3">
      {items.map((item, index) => (
        <span key={item.stepId} className="contents">
          {index > 0 && <span className="hidden sm:inline text-foreground/40" aria-hidden="true">›</span>}
          <SummaryPill
            label={item.label}
            icon={item.icon}
            onClick={() => goToStep(item.stepId)}
            ariaLabel={`Change ${item.stepId.toLowerCase()}`}
          />
        </span>
      ))}
    </div>
  );
}

export default BookingBreadcrumbs;
